// src/pages/inventory/IndividualEquipmentList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Paper,
    Card,
    CardContent,
    InputAdornment,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    QrCodeScanner as ScanIcon,
    FilterList as FilterIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon,
    Engineering as EngineeringIcon,
    Warehouse as WarehouseIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Sort as SortIcon,
    Cancel as CancelIcon,
    ShoppingCart as ShoppingCartIcon,
    Clear as ClearIcon,
    QrCode2 as QrCodeIcon
} from '@mui/icons-material';

const IndividualEquipmentList = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // List & Filters states
    const [equipmentList, setEquipmentList] = useState([]);
    const [items, setItems] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [technicians, setTechnicians] = useState([]);

    // Filter & Sorting parameters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterItem, setFilterItem] = useState('');
    const [filterWarehouse, setFilterWarehouse] = useState('');
    const [filterTechnician, setFilterTechnician] = useState('');
    const [sortBy, setSortBy] = useState('client'); // 'client', 'recent', 'model', 'serial'

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Edit Dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentUnit, setCurrentUnit] = useState({
        id: '',
        serial_number: '',
        mac_address: '',
        condition: 'new',
        status: 'in_stock',
        notes: ''
    });

    const fetchInitialData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            const [itemsRes, warehousesRes, techniciansRes, equipmentRes] = await Promise.all([
                fetch('/api/inventory/items', { headers }),
                fetch('/api/inventory/warehouses', { headers }),
                fetch('/api/technicians', { headers }),
                fetch('/api/inventory/equipment', { headers })
            ]);

            if (itemsRes.ok) setItems(await itemsRes.json());
            if (warehousesRes.ok) setWarehouses(await warehousesRes.json());
            if (techniciansRes.ok) setTechnicians(await techniciansRes.json());
            if (equipmentRes.ok) setEquipmentList(await equipmentRes.json());

        } catch (err) {
            console.error('Error fetching individual equipment data:', err);
            setError('Error al cargar datos del inventario serializado.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const fetchEquipment = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/inventory/equipment', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setEquipmentList(await res.json());
            }
        } catch (err) {
            console.error('Error loading equipment:', err);
        }
    };

    const handleOpenEditDialog = (unit) => {
        setCurrentUnit({
            id: unit.id,
            serial_number: unit.serial_number || '',
            mac_address: unit.mac_address || '',
            condition: unit.condition || 'new',
            status: unit.status || 'in_stock',
            ownership_type: unit.ownership_type || 'rent',
            billing_type: unit.billing_type || 'monthly',
            charged_price: unit.charged_price !== null && unit.charged_price !== undefined ? unit.charged_price : '',
            installment_total: unit.installment_total || '',
            installment_current: unit.installment_current || 0,
            notes: ''
        });
        setEditDialogOpen(true);
    };

    const handleUpdateUnit = async () => {
        try {
            setError(null);
            if (!currentUnit.serial_number || !currentUnit.mac_address) {
                setError('S/N y Dirección MAC son obligatorios.');
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/inventory/equipment/${currentUnit.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    serial_number: currentUnit.serial_number.toUpperCase().trim(),
                    mac_address: currentUnit.mac_address.toUpperCase().trim(),
                    condition: currentUnit.condition,
                    status: currentUnit.status,
                    ownership_type: currentUnit.ownership_type,
                    billing_type: currentUnit.billing_type,
                    charged_price: currentUnit.charged_price !== '' ? parseFloat(currentUnit.charged_price) : 0,
                    installment_total: currentUnit.installment_total !== '' ? parseInt(currentUnit.installment_total) : null,
                    installment_current: currentUnit.installment_current !== '' ? parseInt(currentUnit.installment_current) : 0,
                    notes: currentUnit.notes
                })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Error al actualizar equipo');
            }

            setSuccess('Unidad de equipo actualizada con éxito.');
            setTimeout(() => setSuccess(null), 5000);
            setEditDialogOpen(false);

            await fetchEquipment();
        } catch (err) {
            console.error(err);
            setError(err.message || 'Error al actualizar el equipo.');
        } finally {
            setLoading(false);
        }
    };

    // Filter Equipment List
    const filteredEquipment = equipmentList.filter(eq => {
        if (filterStatus && eq.status !== filterStatus) return false;
        if (filterItem && eq.item_id !== filterItem) return false;
        if (filterWarehouse && eq.warehouse_id !== filterWarehouse) return false;
        if (filterTechnician && eq.technician_id !== filterTechnician) return false;

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const snMatch = eq.serial_number && eq.serial_number.toLowerCase().includes(searchLower);
            const macMatch = eq.mac_address && eq.mac_address.toLowerCase().includes(searchLower);
            const invMatch = eq.invoice_number && eq.invoice_number.toLowerCase().includes(searchLower);
            const itemMatch = eq.item_name && eq.item_name.toLowerCase().includes(searchLower);
            const clientMatch = eq.client_name && eq.client_name.toLowerCase().includes(searchLower);
            const techMatch = eq.technician_name && eq.technician_name.toLowerCase().includes(searchLower);

            if (!(snMatch || macMatch || invMatch || itemMatch || clientMatch || techMatch)) {
                return false;
            }
        }

        return true;
    });

    // Sort / Organize Equipment List
    const sortedEquipment = [...filteredEquipment].sort((a, b) => {
        if (sortBy === 'client') {
            // Group by assignment / Client Name (Installed > Technician > Warehouse > Retired)
            const clientA = a.client_name || a.technician_name || a.warehouse_name || 'ZZZ';
            const clientB = b.client_name || b.technician_name || b.warehouse_name || 'ZZZ';
            return clientA.localeCompare(clientB);
        } else if (sortBy === 'model') {
            const modelA = `${a.item_brand || ''} ${a.item_name || ''}`;
            const modelB = `${b.item_brand || ''} ${b.item_name || ''}`;
            return modelA.localeCompare(modelB);
        } else if (sortBy === 'serial') {
            return (a.serial_number || '').localeCompare(b.serial_number || '');
        } else {
            // Default: Recent first
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        }
    });

    // Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedEquipment = sortedEquipment.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Summary Counts
    const totalUnits = equipmentList.length;
    const inStockUnits = equipmentList.filter(e => e.status === 'in_stock').length;
    const techUnits = equipmentList.filter(e => e.status === 'assigned_to_tech').length;
    const installedUnits = equipmentList.filter(e => e.status === 'installed').length;

    const getConditionBadge = (cond) => {
        switch (cond) {
            case 'new': return <Chip label="Nuevo" size="small" color="success" sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />;
            case 'used': return <Chip label="Usado" size="small" color="warning" sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />;
            case 'reconditioned': return <Chip label="Reacondicionado" size="small" color="info" sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />;
            case 'in_repair': return <Chip label="En Reparación" size="small" color="error" sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />;
            default: return <Chip label={cond || 'Bueno'} size="small" sx={{ height: 20, fontSize: '10px' }} />;
        }
    };

    if (loading && equipmentList.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '65vh', gap: 2 }}>
                <CircularProgress size={48} thickness={4} sx={{ color: '#2563eb' }} />
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Cargando inventario serializado (S/N y MAC)...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3.5 }, bgcolor: '#f8fafc', minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Header Hero Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                    <Box sx={{
                        width: { xs: 44, md: 52 },
                        height: { xs: 44, md: 52 },
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        boxShadow: '0 8px 16px rgba(124, 58, 237, 0.25)',
                        flexShrink: 0
                    }}>
                        <ScanIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                                Control de Equipos Individuales (Series)
                            </Typography>
                            <Chip
                                label={`${totalUnits} Unidades`}
                                size="small"
                                sx={{ bgcolor: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', fontWeight: 800, fontSize: '11px', borderRadius: '6px' }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: 0.2, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                            Trazabilidad unitaria por S/N, MAC y asignación a Cliente / Técnico / Almacén
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'nowrap', width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/inventory/purchases/new')}
                        startIcon={<AddIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            borderRadius: '9px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.5,
                            py: 1,
                            width: { xs: '100%', sm: 'auto' },
                            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
                            '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)' }
                        }}
                    >
                        Ingresar Equipos
                    </Button>

                    <Tooltip title="Actualizar lista">
                        <IconButton
                            onClick={() => fetchInitialData(true)}
                            disabled={refreshing}
                            sx={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '9px',
                                bgcolor: '#fff',
                                p: 1,
                                flexShrink: 0,
                                '&:hover': { bgcolor: '#f1f5f9' }
                            }}
                        >
                            <RefreshIcon sx={{
                                color: '#475569',
                                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Metrics Bar */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ py: 1.8, px: 2, '&:last-child': { pb: 1.8 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', fontSize: '10px' }}>
                                        TOTAL SERIES
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {totalUnits} <Typography component="span" variant="caption" sx={{ color: '#64748b' }}>unidades</Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <QrCodeIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ py: 1.8, px: 2, '&:last-child': { pb: 1.8 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', fontSize: '10px' }}>
                                        EN ALMACÉN
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#2563eb' }}>
                                        {inStockUnits} <Typography component="span" variant="caption" sx={{ color: '#64748b' }}>disponibles</Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <WarehouseIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ py: 1.8, px: 2, '&:last-child': { pb: 1.8 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', fontSize: '10px' }}>
                                        CON TÉCNICOS
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#d97706' }}>
                                        {techUnits} <Typography component="span" variant="caption" sx={{ color: '#64748b' }}>cuadrillas</Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <EngineeringIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ py: 1.8, px: 2, '&:last-child': { pb: 1.8 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', fontSize: '10px' }}>
                                        INSTALADOS (CLIENTES)
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669' }}>
                                        {installedUnits} <Typography component="span" variant="caption" sx={{ color: '#64748b' }}>activos</Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filter & Grouping Bar */}
            <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Grid container spacing={1.5} alignItems="center">
                    {/* Búsqueda */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Buscar por S/N, MAC, Cliente, Técnico, Factura..."
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#94a3b8' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <ClearIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '8px', bgcolor: '#fff' }
                            }}
                        />
                    </Grid>

                    {/* Organizar / Ordenar por */}
                    <Grid item xs={12} sm={6} md={2.5}>
                        <TextField
                            fullWidth
                            select
                            label="Organizar / Ordenar por"
                            size="small"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SortIcon sx={{ color: '#7c3aed' }} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '8px', bgcolor: '#fff', fontWeight: 700 }
                            }}
                        >
                            <MenuItem value="client">👥 Por Cliente / Asignación (A-Z)</MenuItem>
                            <MenuItem value="recent">📅 Más Recientes</MenuItem>
                            <MenuItem value="model">📦 Por Modelo de Equipo (A-Z)</MenuItem>
                            <MenuItem value="serial">🔢 Por Número de Serie (S/N)</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Estado */}
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            fullWidth
                            label="Estado"
                            size="small"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            InputProps={{ sx: { borderRadius: '8px', bgcolor: '#fff' } }}
                        >
                            <MenuItem value="">Todos los Estados</MenuItem>
                            <MenuItem value="in_stock">En Almacén</MenuItem>
                            <MenuItem value="assigned_to_tech">Con Técnico</MenuItem>
                            <MenuItem value="installed">Instalado a Cliente</MenuItem>
                            <MenuItem value="retired">Dado de Baja</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Modelo */}
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            fullWidth
                            label="Modelo"
                            size="small"
                            value={filterItem}
                            onChange={(e) => setFilterItem(e.target.value)}
                            InputProps={{ sx: { borderRadius: '8px', bgcolor: '#fff' } }}
                        >
                            <MenuItem value="">Todos los Modelos</MenuItem>
                            {items.filter(i => i.equipment_type).map(item => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.brand} - {item.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Almacén */}
                    <Grid item xs={12} sm={6} md={1.5}>
                        <TextField
                            select
                            fullWidth
                            label="Almacén"
                            size="small"
                            value={filterWarehouse}
                            onChange={(e) => setFilterWarehouse(e.target.value)}
                            InputProps={{ sx: { borderRadius: '8px', bgcolor: '#fff' } }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {warehouses.map(wh => (
                                <MenuItem key={wh.id} value={wh.id}>
                                    {wh.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* DESKTOP VIEW: Custom Table (Visible on md and up) */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Número de Serie (S/N)</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Dirección MAC</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Equipo / Modelo</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Asignación y Ubicación</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Condición</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Costo WISP vs Cobro Cliente</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedEquipment.length > 0 ? (
                                    paginatedEquipment.map((eq) => {
                                        const cPrice = parseFloat(eq.charged_price || 0);
                                        const instTotal = parseInt(eq.installment_total || 0);
                                        const instCurr = parseInt(eq.installment_current || 0);

                                        return (
                                            <TableRow key={eq.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                {/* S/N */}
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#2563eb', letterSpacing: '0.02em' }}>
                                                        {eq.serial_number}
                                                    </Typography>
                                                </TableCell>

                                                {/* MAC Address */}
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#334155', bgcolor: '#f1f5f9', px: 0.8, py: 0.3, borderRadius: '4px', display: 'inline-block' }}>
                                                        {eq.mac_address || 'Sin MAC'}
                                                    </Typography>
                                                </TableCell>

                                                {/* Equipo / Modelo */}
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                            {eq.item_name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.2 }}>
                                                            {eq.item_brand && (
                                                                <Chip label={eq.item_brand} size="small" variant="outlined" sx={{ height: 18, fontSize: '9px', fontWeight: 700 }} />
                                                            )}
                                                            {eq.equipment_type && (
                                                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                    {eq.equipment_type}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>

                                                {/* Asignación y Ubicación (Relacionado con Cliente / Técnico / Almacén) */}
                                                <TableCell>
                                                    {eq.status === 'installed' ? (
                                                        <Chip
                                                            icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                                                            label={`Cliente: ${eq.client_name || 'Instalado'}`}
                                                            size="small"
                                                            color="success"
                                                            sx={{ height: 26, fontSize: '11px', fontWeight: 700, borderRadius: '8px' }}
                                                        />
                                                    ) : eq.status === 'assigned_to_tech' ? (
                                                        <Chip
                                                            icon={<EngineeringIcon sx={{ fontSize: '14px !important' }} />}
                                                            label={`Técnico: ${eq.technician_name || 'Cargado'}`}
                                                            size="small"
                                                            color="warning"
                                                            sx={{ height: 26, fontSize: '11px', fontWeight: 700, borderRadius: '8px' }}
                                                        />
                                                    ) : eq.status === 'in_stock' ? (
                                                        <Chip
                                                            icon={<WarehouseIcon sx={{ fontSize: '14px !important' }} />}
                                                            label={`Almacén: ${eq.warehouse_name || 'Central'}`}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ height: 26, fontSize: '11px', fontWeight: 700, borderRadius: '8px' }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
                                                            label="Dado de Baja"
                                                            size="small"
                                                            color="error"
                                                            sx={{ height: 26, fontSize: '11px', fontWeight: 700, borderRadius: '8px' }}
                                                        />
                                                    )}
                                                </TableCell>

                                                {/* Condición Física */}
                                                <TableCell>
                                                    {getConditionBadge(eq.condition)}
                                                </TableCell>

                                                {/* Costo WISP vs Cobro Cliente */}
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '11px' }}>
                                                        Costo WISP: <strong>S/ {parseFloat(eq.acquisition_cost || 0).toFixed(2)}</strong>
                                                    </Typography>
                                                    {eq.ownership_type === 'rent' || cPrice > 0 ? (
                                                        <Chip
                                                            label={
                                                                eq.billing_type === 'installments' && instTotal > 0
                                                                    ? `Alquiler S/ ${cPrice.toFixed(2)} (${instCurr}/${instTotal} cuotas)`
                                                                    : `Alquiler S/ ${cPrice.toFixed(2)}/mes`
                                                            }
                                                            size="small"
                                                            sx={{ height: 22, fontSize: '10px', fontWeight: 800, bgcolor: '#f3e8ff', color: '#6b21a8', mt: 0.4 }}
                                                        />
                                                    ) : eq.ownership_type === 'sale' ? (
                                                        <Chip label="Venta Cliente" size="small" sx={{ height: 22, fontSize: '10px', fontWeight: 700, bgcolor: '#ecfdf5', color: '#047857', mt: 0.4 }} />
                                                    ) : (
                                                        <Chip label="Comodato Gratuito" size="small" sx={{ height: 22, fontSize: '10px', fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569', mt: 0.4 }} />
                                                    )}
                                                    {eq.invoice_number && (
                                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                            Fact: {eq.invoice_number}
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Acciones */}
                                                <TableCell align="right">
                                                    <Tooltip title="Editar / Trazabilidad">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenEditDialog(eq)}
                                                            sx={{ color: '#2563eb', bgcolor: 'rgba(37, 99, 235, 0.08)', '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.18)' } }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                            <ScanIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569' }}>
                                                No se encontraron equipos serializados
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                                                Intenta cambiar los términos de búsqueda o registra la compra de nuevos equipos.
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => navigate('/inventory/purchases/new')}
                                                startIcon={<AddIcon />}
                                                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Ingresar Compras / Series
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={sortedEquipment.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Filas por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        sx={{ borderTop: '1px solid #f1f5f9' }}
                    />
                </Paper>
            </Box>

            {/* MOBILE VIEW: Equipment Cards List (Visible on mobile xs and sm - ZERO horizontal scroll) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.8 }}>
                {paginatedEquipment.length > 0 ? (
                    paginatedEquipment.map((eq) => {
                        return (
                            <Paper
                                key={eq.id}
                                sx={{
                                    p: 2,
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',
                                    bgcolor: '#fff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                                    boxSizing: 'border-box',
                                    width: '100%'
                                }}
                            >
                                {/* Top Header: S/N & Status Badge */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#2563eb', letterSpacing: '0.02em', lineHeight: 1.2 }}>
                                            S/N: {eq.serial_number}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#475569', fontWeight: 700, display: 'block', mt: 0.2 }}>
                                            MAC: {eq.mac_address || 'Sin MAC'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ flexShrink: 0 }}>
                                        {getConditionBadge(eq.condition)}
                                    </Box>
                                </Box>

                                {/* Location & Client Badge */}
                                <Box sx={{ my: 1, py: 1, borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 700, mb: 0.4 }}>
                                        Ubicación / Asignación:
                                    </Typography>
                                    {eq.status === 'installed' ? (
                                        <Chip
                                            icon={<PersonIcon sx={{ fontSize: '13px !important' }} />}
                                            label={`Cliente: ${eq.client_name || 'Instalado'}`}
                                            size="small"
                                            color="success"
                                            sx={{ fontWeight: 700, fontSize: '11px', height: 24 }}
                                        />
                                    ) : eq.status === 'assigned_to_tech' ? (
                                        <Chip
                                            icon={<EngineeringIcon sx={{ fontSize: '13px !important' }} />}
                                            label={`Técnico: ${eq.technician_name || 'Cargado'}`}
                                            size="small"
                                            color="warning"
                                            sx={{ fontWeight: 700, fontSize: '11px', height: 24 }}
                                        />
                                    ) : eq.status === 'in_stock' ? (
                                        <Chip
                                            icon={<WarehouseIcon sx={{ fontSize: '13px !important' }} />}
                                            label={`Almacén: ${eq.warehouse_name || 'Central'}`}
                                            size="small"
                                            color="primary"
                                            sx={{ fontWeight: 700, fontSize: '11px', height: 24 }}
                                        />
                                    ) : (
                                        <Chip label="Dado de Baja" size="small" color="error" sx={{ fontWeight: 700, fontSize: '11px', height: 24 }} />
                                    )}
                                </Box>

                                {/* Bottom Info Row */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '12px' }}>
                                            {eq.item_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                            Costo: ${parseFloat(eq.acquisition_cost || 0).toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleOpenEditDialog(eq)}
                                        startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', py: 0.4, px: 1.2, fontSize: '12px', color: '#2563eb' }}
                                    >
                                        Editar
                                    </Button>
                                </Box>
                            </Paper>
                        );
                    })
                ) : (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px' }}>
                        <ScanIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569' }}>
                            Sin equipos encontrados
                        </Typography>
                    </Paper>
                )}

                {/* Mobile Pagination */}
                <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', mt: 1 }}>
                    <TablePagination
                        component="div"
                        count={sortedEquipment.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage="Filas:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                </Paper>
            </Box>

            {/* Modal Dialog for Edit Equipment Traceability */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', margin: { xs: 1.5, sm: 2 } } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ScanIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                            Editar Unidad Física de Equipo
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setEditDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ pt: 1 }}>
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Número de Serie (S/N)"
                                    value={currentUnit.serial_number}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, serial_number: e.target.value }))}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Dirección MAC"
                                    value={currentUnit.mac_address}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, mac_address: e.target.value }))}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Condición Física"
                                    value={currentUnit.condition}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, condition: e.target.value }))}
                                >
                                    <MenuItem value="new">Buena / Nueva</MenuItem>
                                    <MenuItem value="used">Usada</MenuItem>
                                    <MenuItem value="reconditioned">Reacondicionada</MenuItem>
                                    <MenuItem value="in_repair">En Reparación</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Estado de Trazabilidad"
                                    value={currentUnit.status}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    <MenuItem value="in_stock" disabled={currentUnit.status !== 'retired' && currentUnit.status !== 'in_stock'}>
                                        Disponible en Almacén
                                    </MenuItem>
                                    <MenuItem value="assigned_to_tech" disabled>
                                        Asignado a Técnico
                                    </MenuItem>
                                    <MenuItem value="installed" disabled>
                                        Instalado a Cliente
                                    </MenuItem>
                                    <MenuItem value="retired">
                                        Retirado (Dar de Baja de Inventario)
                                    </MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 0.5 }}>
                                    <Chip label="Configuración de Cobro al Cliente (Alquiler / Venta)" size="small" sx={{ fontWeight: 700, fontSize: '11px' }} />
                                </Divider>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Propiedad del Equipo"
                                    value={currentUnit.ownership_type || 'rent'}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, ownership_type: e.target.value }))}
                                >
                                    <MenuItem value="rent">Alquiler (Propiedad WISP)</MenuItem>
                                    <MenuItem value="sale">Venta (Propiedad Cliente)</MenuItem>
                                    <MenuItem value="free_loan">Comodato Gratuito</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Tipo de Facturación"
                                    value={currentUnit.billing_type || 'monthly'}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, billing_type: e.target.value }))}
                                >
                                    <MenuItem value="monthly">Mensual Continuo</MenuItem>
                                    <MenuItem value="installments">En Cuotas</MenuItem>
                                    <MenuItem value="one_time">Pago Único</MenuItem>
                                    <MenuItem value="none">Sin Cobro</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Monto Cobrado (S/)"
                                    value={currentUnit.charged_price}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, charged_price: e.target.value }))}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">S/</InputAdornment>
                                    }}
                                />
                            </Grid>

                            {currentUnit.billing_type === 'installments' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Cuota Actual Pagada"
                                            value={currentUnit.installment_current}
                                            onChange={(e) => setCurrentUnit(prev => ({ ...prev, installment_current: e.target.value }))}
                                            helperText="Ej. 3 (mes actual)"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Total de Cuotas Convenidas"
                                            value={currentUnit.installment_total}
                                            onChange={(e) => setCurrentUnit(prev => ({ ...prev, installment_total: e.target.value }))}
                                            helperText="Ej. 18 cuotas"
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Notas / Motivo de actualización o baja"
                                    placeholder="Describa el motivo por el cual da de baja el equipo o actualiza sus series..."
                                    value={currentUnit.notes}
                                    onChange={(e) => setCurrentUnit(prev => ({ ...prev, notes: e.target.value }))}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleUpdateUnit}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3
                        }}
                    >
                        Guardar Cambios
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IndividualEquipmentList;
